import { useEffect, useRef, useState } from 'react';
import { Button } from '@/kit/Button';
import { ClosePopup } from '@/kit/ClosePopup';
import { Icon } from '@/kit/Icon';
import { TabGroup } from '@/kit/Tabs';
import { Toggle } from '@/kit/Toggle';
import { openDialog } from '@/kit/dialog';
import { NotificationTextMessage } from '@/core/enums/notification';
import {
  CompanyContactType,
  companyContactTypeNames,
  type CompanyContactDialogData,
  type CompanyContactOptions,
  type CompanyContactRow,
  type SaveCompanyContactResponse,
} from '@/core/models/report-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { reportSettingsService } from '@/core/services/reportSettings';
import { CompanyContactDialog } from './CompanyContactDialog';
import { CompanyContactGrid, type ContactSelectionChange } from './CompanyContactGrid';
import { ReportSettingsFrame, useReportClient } from './common';
import { ClientKind, useClientKind } from './clientKind';
import './CompanyContact.scss';

export type ContactOptionKey = Exclude<keyof CompanyContactOptions, 'clientId'>;

/** Report Settings > Company Contact (LLP Contacts for an LLP): four tabs of contacts, reporting options that save as they are ticked. */
export function CompanyContact() {
  const client = useReportClient();
  const kind = useClientKind(client?.id);
  const isPartnership = kind?.clientType === ClientKind.Partnership;
  const isSoleTrader = kind?.clientType === ClientKind.SoleTrader;
  const isLlp = kind?.clientType === ClientKind.Llp;
  // BUG-078: a partnership's partners, an LLP's members and a sole trader's lack of either.
  // A sole trader's owner is stored as a director (the API has no proprietor type) but shown as the proprietor.
  const principalLabel = isPartnership ? 'Partner' : isLlp ? 'Member' : isSoleTrader ? 'Proprietor' : undefined;
  const [options, setOptionsState] = useState<CompanyContactOptions | null>(null);
  const [contacts, setContacts] = useState<CompanyContactRow[]>([]);
  // BUG-029: which contact types take a first and a last name is the server's rule, sent with the contacts.
  const [personNameTypes, setPersonNameTypes] = useState<number[] | null>(null);
  const optionsRef = useRef<CompanyContactOptions | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const setOptions = (next: CompanyContactOptions | null) => {
    optionsRef.current = next;
    setOptionsState(next);
  };

  const load = () => {
    if (!client) return;
    reportSettingsService.getCompanyContacts(client.id).then(
      (view) => {
        if (!alive.current) return;
        setOptions(view.options);
        setContacts(view.contacts);
        setPersonNameTypes(view.personNameContactTypes ?? []);
      },
      (error) => alive.current && notifyError(error),
    );
  };

  useEffect(() => {
    load();
    // Loads once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ofType = (type: number) => contacts.filter((c) => c.contactType === type);

  const setOption = (key: ContactOptionKey, value: boolean) => {
    const current = optionsRef.current;
    if (!current) return;
    const previous = current[key];
    const next = { ...current, [key]: value };
    setOptions(next);
    reportSettingsService.saveContactOptions(next).then(
      (saved) => {
        setOptions(saved);
        notifySuccess('Reporting options saved successfully.');
      },
      (error) => {
        setOptions({ ...optionsRef.current!, [key]: previous });
        notifyError(error);
      },
    );
  };

  const addNewDialog = (contactType: number, contact?: CompanyContactRow) => {
    if (!client) return;
    // BUG-029: directors, secretaries and solicitors of a company take a first and a last name; BUG-078: so does a
    // partner (the old app's Name and Last Name), while an LLP member keeps one "Full Name".
    const asPrincipal = contactType === CompanyContactType.Director && !!principalLabel;
    const isPerson =
      contactType === CompanyContactType.Director ||
      contactType === CompanyContactType.Secretary ||
      contactType === CompanyContactType.Solicitor;
    const usesPersonName =
      (!asPrincipal || isPartnership || isSoleTrader) &&
      (personNameTypes !== null ? personNameTypes.includes(contactType) : isPerson);
    const data: CompanyContactDialogData = {
      clientId: client.id,
      contactType,
      contact: contact ?? null,
      usesPersonName,
    };
    if (asPrincipal) {
      data.principalLabel = principalLabel;
      data.partnerTerms = isPartnership;
      // An LLP member's share is always a percentage of the LLP, so there is no "Share in percentage" choice for it.
      data.shareAsPercentage = isLlp;
    }
    openDialog<CompanyContactDialogData, SaveCompanyContactResponse>(CompanyContactDialog, {
      disableClose: true,
      panelClass: 'cst-invite',
      data,
    })
      .afterClosed()
      .then((result) => {
        if (result) load();
      });
  };

  const onDelete = (contact: CompanyContactRow) => {
    openDialog<{ itemName: string; count: number }, boolean>(ClosePopup, {
      data: {
        itemName:
          (contact.contactType === CompanyContactType.Director ? principalLabel : undefined) ??
          companyContactTypeNames[contact.contactType] ??
          'Contact',
        count: 1,
      },
    })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) return;
        reportSettingsService.deleteCompanyContacts([contact.id]).then(
          () => {
            notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
            load();
          },
          (error) => notifyError(error),
        );
      });
  };

  const patchSelected = (values: Map<string, boolean>) =>
    setContacts((current) =>
      current.map((row) => (values.has(row.id) ? { ...row, isSelected: values.get(row.id)! } : row)),
    );

  const onSelectionChange = (contactType: number, rows: CompanyContactRow[], change: ContactSelectionChange) => {
    if (!client) return;
    const affected = change.ids.length === 0 ? rows : rows.filter((r) => change.ids.includes(r.id));
    const previous = new Map(affected.map((r) => [r.id, r.isSelected]));
    patchSelected(new Map(affected.map((r) => [r.id, change.isSelected])));
    reportSettingsService
      .selectCompanyContacts(client.id, contactType, change.ids, change.isSelected)
      .catch((error) => {
        patchSelected(previous);
        notifyError(error);
      });
  };

  // 2 Nov design review: the reporting options are toggle switches (they still save as they are switched).
  const optionBox = (key: ContactOptionKey, label: string) => (
    <Toggle disabled={!options} checked={Boolean(options?.[key])} onCheckedChange={(on) => setOption(key, on)}>
      {label}
    </Toggle>
  );

  /**
   * task-79: every one of these options is read when the Accounts report is generated, and only
   * "Share in percentage" also changes the table on this screen. Without saying so, toggling any
   * of the others looks like it did nothing at all.
   */
  const reportOnlyHint = (text: string) => <p className="font-size-12 text-gray mb-0 mt-5">{text}</p>;

  /** What each tab's table lists, for its empty message; undefined until the contacts have been read. */
  const emptyLabelOf = (type: number): string | undefined => {
    if (!options) return undefined;
    if (type === CompanyContactType.Director) {
      return isPartnership ? 'partners' : isLlp ? 'members' : isSoleTrader ? 'proprietors' : 'directors';
    }
    return type === CompanyContactType.Secretary ? 'secretaries' : type === CompanyContactType.Solicitor ? 'solicitors' : 'bankers';
  };

  const grid = (type: number, rows: CompanyContactRow[]) => (
    <CompanyContactGrid
      emptyLabel={emptyLabelOf(type)}
      contacts={rows}
      contactType={type}
      shareInPercentage={isLlp || !!options?.directorsShareInPercentage}
      partnerTerms={isPartnership && type === CompanyContactType.Director}
      onEdit={(row) => addNewDialog(type, row)}
      onDelete={onDelete}
      onSelectionChange={(change) => onSelectionChange(type, rows, change)}
    />
  );

  const addButton = (type: number, label: string) => (
    <Button className="action-button primary-button border-radius-6" onClick={() => addNewDialog(type)}>
      <Icon name="add" />
      <span>{label}</span>
    </Button>
  );

  const directors = ofType(CompanyContactType.Director);
  const secretaries = ofType(CompanyContactType.Secretary);
  const solicitors = ofType(CompanyContactType.Solicitor);
  const bankers = ofType(CompanyContactType.Banker);

  const infoPageLabel = isLlp ? 'Display on LLP information page' : 'Display on company information page';

  const directorsTab = {
    label: isPartnership ? 'Partners' : isLlp ? 'Members' : isSoleTrader ? 'Proprietors' : 'Directors',
    children:
      isPartnership || isSoleTrader ? (
        <>
          <div className="d-flex align-items-center justify-space-between pb-1 pt-5">
            <div>{isPartnership && optionBox('directorsOnCompanyInfo', 'Display on company information page')}</div>
            {addButton(CompanyContactType.Director, isSoleTrader ? 'Add New Proprietor' : 'Add New Partner')}
          </div>
          {grid(CompanyContactType.Director, directors)}
        </>
      ) : (
        <>
          <div className="contact-options">
            {/* 3 Nov design review: the Add button sits on the title row's right edge (the table's right edge), the options below it in an even grid. */}
            <div className="contact-options__head">
              <div className="form-field-title">
                <p>Reporting Options</p>
              </div>
              {addButton(CompanyContactType.Director, isLlp ? 'Add New Member' : 'Add New Director')}
            </div>
            <div>
              <div className="dir-box">
                {optionBox(
                  'directorsOnCompanyInfo',
                  isLlp ? 'Display on LLP information page' : 'Display on company information page',
                )}
                {optionBox('directorsShowPeriod', isLlp ? "Display member's period" : "Display director's period")}
                {/* An LLP member's share is always a percentage, so there is no "Share in percentage" choice for it. */}
                {!isLlp && optionBox('directorsShareInPercentage', 'Share in percentage')}
                {optionBox('directorsShowShares', isLlp ? "Display member's shares" : "Display director's shares")}
                {optionBox(
                  'directorsCurrentOnly',
                  isLlp
                    ? 'Display current members on LLP Information page'
                    : 'Display current directors on Company Information page',
                )}
              </div>
              {reportOnlyHint(
                isLlp
                  ? 'These options apply when the Accounts report is generated.'
                  : 'These options apply when the Accounts report is generated. Only “Share in percentage” also changes the table below.',
              )}
            </div>
          </div>
          {grid(CompanyContactType.Director, directors)}
        </>
      ),
  };

  const secretariesTab = {
    label: 'Secretaries',
    children: (
      <>
        <div className="d-flex align-items-center justify-space-between pb-1 pt-5">
          <div>
            {optionBox('secretariesOnCompanyInfo', 'Display on company information page')}
            {reportOnlyHint('This option applies when the Accounts report is generated, not on this screen.')}
          </div>
          {addButton(CompanyContactType.Secretary, 'Add New Secretary')}
        </div>
        {grid(CompanyContactType.Secretary, secretaries)}
      </>
    ),
  };

  const solicitorsTab = {
    label: 'Solicitors',
    children: (
      <>
        <div className="d-flex align-items-center justify-space-between pb-1 pt-5">
          <div>
            {optionBox('solicitorsOnCompanyInfo', infoPageLabel)}
            {reportOnlyHint('This option applies when the Accounts report is generated, not on this screen.')}
          </div>
          {addButton(CompanyContactType.Solicitor, 'Add New Solicitor')}
        </div>
        {grid(CompanyContactType.Solicitor, solicitors)}
      </>
    ),
  };

  const bankersTab = {
    label: 'Bankers',
    children: (
      <>
        <div className="d-flex align-items-center justify-space-between pb-1 pt-5">
          <div>
            {optionBox('bankersOnCompanyInfo', infoPageLabel)}
            {reportOnlyHint('This option applies when the Accounts report is generated, not on this screen.')}
          </div>
          {addButton(CompanyContactType.Banker, 'Add New Banker')}
        </div>
        {grid(CompanyContactType.Banker, bankers)}
      </>
    ),
  };

  // BUG-078: a partnership has Partners, Solicitors and Bankers; a sole trader has a Proprietor (stored as the director contact) and no secretary.
  // BUG-072: an LLP has Members (not Directors) and no company secretary, so no Secretaries tab.
  const tabs =
    isPartnership || isLlp
      ? [directorsTab, solicitorsTab, bankersTab]
      : isSoleTrader
        ? [directorsTab, solicitorsTab, bankersTab]
        : [directorsTab, secretariesTab, solicitorsTab, bankersTab];

  return (
    <ReportSettingsFrame
      title={isPartnership || isSoleTrader ? 'Business Contacts' : isLlp ? 'LLP Contacts' : 'Company Contacts'}
      hostClassName="company-contact-host"
    >
      <div className="sidenav-body">
        <TabGroup alignTabs="start" tabs={tabs} />
      </div>
    </ReportSettingsFrame>
  );
}
